import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CreateDemandForecastParams,
  CreateDemandForecastResponseSchema,
  DemandForecastCalendarBucket,
  DemandForecastCalendarPeriod,
  DemandForecastCalendarResponseSchema,
  GetDemandForecastResponseSchema,
  HydratedDemandForecast,
  ListDemandForecastsResponseSchema,
  UpdateDemandForecastParams,
  UpdateDemandForecastResponseSchema,
} from "@forgepoint/shared";

interface DemandForecastLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface DemandForecastsStore {
  demandForecasts: Map<string, HydratedDemandForecast>;
  demandForecastLoadingStates: Map<string, DemandForecastLoadingState>;
  listLoading: boolean;
  listError: string | null;
  calendarLoading: boolean;
  calendarError: string | null;
  calendarBuckets: DemandForecastCalendarPeriod[];

  getDemandForecast: (id: string) => HydratedDemandForecast | undefined;
  getDemandForecastLoadingState: (id: string) => DemandForecastLoadingState;
  fetchDemandForecasts: (params?: {
    search?: string;
    opportunityId?: string;
    locationId?: string;
    itemId?: string;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
    offset?: number;
  }) => Promise<{ ids: string[]; total: number }>;
  fetchDemandForecast: (id: string) => Promise<void>;
  createDemandForecast: (data: CreateDemandForecastParams) => Promise<{
    demandForecast: HydratedDemandForecast | null;
    error: string | null;
  }>;
  updateDemandForecast: (
    id: string,
    data: UpdateDemandForecastParams,
  ) => Promise<{
    demandForecast: HydratedDemandForecast | null;
    error: string | null;
  }>;
  deleteDemandForecast: (
    id: string,
  ) => Promise<{ success: boolean; error?: string }>;
  fetchCalendar: (params: {
    startDate: Date;
    endDate: Date;
    locationId?: string;
    bucket: DemandForecastCalendarBucket;
  }) => Promise<void>;
}

const getInitialLoadingState = (): DemandForecastLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

export const useDemandForecastsStore = create<DemandForecastsStore>(
  (set, get) => ({
    demandForecasts: new Map(),
    demandForecastLoadingStates: new Map(),
    listLoading: false,
    listError: null,
    calendarLoading: false,
    calendarError: null,
    calendarBuckets: [],

    getDemandForecast: (id) => get().demandForecasts.get(id),
    getDemandForecastLoadingState: (id) =>
      get().demandForecastLoadingStates.get(id) || getInitialLoadingState(),

    fetchDemandForecasts: async (params) => {
      set({ listLoading: true, listError: null });

      try {
        const searchParams = new URLSearchParams();
        if (params?.search) {
          searchParams.set("search", params.search);
        }
        if (params?.opportunityId) {
          searchParams.set("opportunityId", params.opportunityId);
        }
        if (params?.locationId) {
          searchParams.set("locationId", params.locationId);
        }
        if (params?.itemId) {
          searchParams.set("itemId", params.itemId);
        }
        if (params?.startDate) {
          searchParams.set("startDate", params.startDate.toISOString());
        }
        if (params?.endDate) {
          searchParams.set("endDate", params.endDate.toISOString());
        }
        if (params?.limit !== undefined) {
          searchParams.set("limit", String(params.limit));
        }
        if (params?.offset !== undefined) {
          searchParams.set("offset", String(params.offset));
        }

        const queryString = searchParams.toString();
        const response = await fetchApi(
          queryString ? `demand-forecasts?${queryString}` : "demand-forecasts",
          "GET",
        );

        if (response.ok && response.data) {
          const data = ListDemandForecastsResponseSchema.parse(response.data);
          set((state) => {
            const demandForecasts = new Map(state.demandForecasts);
            data.demandForecasts.forEach((forecast) => {
              demandForecasts.set(forecast.id, forecast);
            });
            return { demandForecasts, listLoading: false };
          });
          return {
            ids: data.demandForecasts.map((forecast) => forecast.id),
            total: data.total,
          };
        }

        set({
          listLoading: false,
          listError: response.error ?? "Failed to fetch demand forecasts",
        });
        return { ids: [], total: 0 };
      } catch (error) {
        captureException(error as Error);
        set({
          listLoading: false,
          listError: "Failed to fetch demand forecasts",
        });
        return { ids: [], total: 0 };
      }
    },

    fetchDemandForecast: async (id) => {
      set((state) => {
        const demandForecastLoadingStates = new Map(
          state.demandForecastLoadingStates,
        );
        const currentState =
          demandForecastLoadingStates.get(id) || getInitialLoadingState();
        demandForecastLoadingStates.set(id, {
          ...currentState,
          isLoadingGet: true,
          getError: null,
        });
        return { demandForecastLoadingStates };
      });

      try {
        const response = await fetchApi(`demand-forecasts/${id}`, "GET");
        if (response.ok && response.data) {
          const data = GetDemandForecastResponseSchema.parse(response.data);
          set((state) => {
            const demandForecasts = new Map(state.demandForecasts);
            const demandForecastLoadingStates = new Map(
              state.demandForecastLoadingStates,
            );
            const currentState =
              demandForecastLoadingStates.get(id) || getInitialLoadingState();
            demandForecasts.set(data.demandForecast.id, data.demandForecast);
            demandForecastLoadingStates.set(id, {
              ...currentState,
              isLoadingGet: false,
              getError: null,
            });
            return { demandForecasts, demandForecastLoadingStates };
          });
          return;
        }

        set((state) => {
          const demandForecastLoadingStates = new Map(
            state.demandForecastLoadingStates,
          );
          const currentState =
            demandForecastLoadingStates.get(id) || getInitialLoadingState();
          demandForecastLoadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: response.error ?? "Failed to fetch demand forecast",
          });
          return { demandForecastLoadingStates };
        });
      } catch (error) {
        captureException(error as Error);
        set((state) => {
          const demandForecastLoadingStates = new Map(
            state.demandForecastLoadingStates,
          );
          const currentState =
            demandForecastLoadingStates.get(id) || getInitialLoadingState();
          demandForecastLoadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: "Failed to fetch demand forecast",
          });
          return { demandForecastLoadingStates };
        });
      }
    },

    createDemandForecast: async (data) => {
      try {
        const response = await fetchApi("demand-forecasts", "POST", {
          body: data,
        });
        if (response.ok && response.data) {
          const result = CreateDemandForecastResponseSchema.parse(
            response.data,
          );
          set((state) => {
            const demandForecasts = new Map(state.demandForecasts);
            demandForecasts.set(
              result.demandForecast.id,
              result.demandForecast,
            );
            return { demandForecasts };
          });
          return { demandForecast: result.demandForecast, error: null };
        }
        return {
          demandForecast: null,
          error: response.error ?? "Failed to create demand forecast",
        };
      } catch (error) {
        captureException(error as Error);
        return {
          demandForecast: null,
          error: "Failed to create demand forecast",
        };
      }
    },

    updateDemandForecast: async (id, data) => {
      set((state) => {
        const demandForecastLoadingStates = new Map(
          state.demandForecastLoadingStates,
        );
        const currentState =
          demandForecastLoadingStates.get(id) || getInitialLoadingState();
        demandForecastLoadingStates.set(id, {
          ...currentState,
          isLoadingSave: true,
          saveError: null,
        });
        return { demandForecastLoadingStates };
      });

      try {
        const response = await fetchApi(`demand-forecasts/${id}`, "PUT", {
          body: data,
        });
        if (response.ok && response.data) {
          const result = UpdateDemandForecastResponseSchema.parse(
            response.data,
          );
          set((state) => {
            const demandForecasts = new Map(state.demandForecasts);
            const demandForecastLoadingStates = new Map(
              state.demandForecastLoadingStates,
            );
            const currentState =
              demandForecastLoadingStates.get(id) || getInitialLoadingState();
            demandForecasts.set(
              result.demandForecast.id,
              result.demandForecast,
            );
            demandForecastLoadingStates.set(id, {
              ...currentState,
              isLoadingSave: false,
              saveError: null,
            });
            return { demandForecasts, demandForecastLoadingStates };
          });
          return { demandForecast: result.demandForecast, error: null };
        }

        const error = response.error ?? "Failed to update demand forecast";
        set((state) => {
          const demandForecastLoadingStates = new Map(
            state.demandForecastLoadingStates,
          );
          const currentState =
            demandForecastLoadingStates.get(id) || getInitialLoadingState();
          demandForecastLoadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: error,
          });
          return { demandForecastLoadingStates };
        });
        return { demandForecast: null, error };
      } catch (error) {
        captureException(error as Error);
        return {
          demandForecast: null,
          error: "Failed to update demand forecast",
        };
      }
    },

    deleteDemandForecast: async (id) => {
      set((state) => {
        const demandForecastLoadingStates = new Map(
          state.demandForecastLoadingStates,
        );
        const currentState =
          demandForecastLoadingStates.get(id) || getInitialLoadingState();
        demandForecastLoadingStates.set(id, {
          ...currentState,
          isLoadingDelete: true,
          deleteError: null,
        });
        return { demandForecastLoadingStates };
      });

      try {
        const response = await fetchApi(`demand-forecasts/${id}`, "DELETE");
        if (response.ok) {
          set((state) => {
            const demandForecasts = new Map(state.demandForecasts);
            const demandForecastLoadingStates = new Map(
              state.demandForecastLoadingStates,
            );
            demandForecasts.delete(id);
            demandForecastLoadingStates.delete(id);
            return { demandForecasts, demandForecastLoadingStates };
          });
          return { success: true };
        }

        const error = response.error ?? "Failed to delete demand forecast";
        set((state) => {
          const demandForecastLoadingStates = new Map(
            state.demandForecastLoadingStates,
          );
          const currentState =
            demandForecastLoadingStates.get(id) || getInitialLoadingState();
          demandForecastLoadingStates.set(id, {
            ...currentState,
            isLoadingDelete: false,
            deleteError: error,
          });
          return { demandForecastLoadingStates };
        });
        return { success: false, error };
      } catch (error) {
        captureException(error as Error);
        const errorMessage = "Failed to delete demand forecast";
        set((state) => {
          const demandForecastLoadingStates = new Map(
            state.demandForecastLoadingStates,
          );
          const currentState =
            demandForecastLoadingStates.get(id) || getInitialLoadingState();
          demandForecastLoadingStates.set(id, {
            ...currentState,
            isLoadingDelete: false,
            deleteError: errorMessage,
          });
          return { demandForecastLoadingStates };
        });
        return { success: false, error: errorMessage };
      }
    },

    fetchCalendar: async (params) => {
      set({ calendarLoading: true, calendarError: null });
      try {
        const searchParams = new URLSearchParams({
          startDate: params.startDate.toISOString(),
          endDate: params.endDate.toISOString(),
          bucket: params.bucket,
        });
        if (params.locationId) {
          searchParams.set("locationId", params.locationId);
        }

        const response = await fetchApi(
          `demand-forecasts/calendar?${searchParams.toString()}`,
          "GET",
        );
        if (response.ok && response.data) {
          const data = DemandForecastCalendarResponseSchema.parse(
            response.data,
          );
          set({
            calendarBuckets: data.buckets,
            calendarLoading: false,
            calendarError: null,
          });
          return;
        }

        set({
          calendarLoading: false,
          calendarError: response.error ?? "Failed to fetch forecast calendar",
        });
      } catch (error) {
        captureException(error as Error);
        set({
          calendarLoading: false,
          calendarError: "Failed to fetch forecast calendar",
        });
      }
    },
  }),
);
